import { useCallback } from "react";
import { useDataStateSelector, useDataSetStatus } from "./useData";
import { useMainContext } from "./useMainContext";
import { useNavigate } from "react-router-dom";
import { db } from "../db";
import { draft } from "./draft";
import { planNavigation } from "./draft/navigation";
import useUserFields from "./useUserFields";

const propertyTitle = (id) => db.peek("properties", id)?.title;

// What leaving for destination does (planNavigation's plan: { action,
// formStashed, discard, message, warnings }), once the open forms whose
// draft scope the destination stays in were asked to put their changes into
// the draft (draft.stashOpenForms: a side effect). The same rules for the
// app's navigations (useConfirmNavigation) and for the browser's Back and
// Forward (NavigationGuard). formDirty, formScope: unsavedData and
// unsavedScope of the data state; branch: the user's.
export function planLeave({
  destination,
  leaveAll = false,
  formDirty = false,
  formScope = null,
  branch
}) {
  // Nothing is drafted on main, nor kept by a navigation that leaves every
  // scope or is not one
  const stash =
    formDirty && destination && !leaveAll && branch !== "main"
      ? draft.stashOpenForms(destination)
      : null;

  return planNavigation({
    formDirty,
    stash,
    formScope,
    // After the stash: the drafts it changed stay on the destination
    draftsLeft: draft.scopesLeftBy(leaveAll ? null : destination),
    navigating: Boolean(destination) || leaveAll,
    isDirty: draft.isDirty,
    propertyTitle
  });
}

// Carries out a plan of planLeave: carryOut(plan, decide). A form stashed is
// not unsaved any more (its status is cleared); "block": the warnings, then
// decide(false); "go": decide(true); "ask": the confirmation, then
// decide(proceed, discard): on "Exit Without Saving" the caller runs
// discard() (the drafts the plan leaves, and the unsaved status of the
// form) when it navigates.
export function useCarryOutLeave({ statusField = "unsavedData" } = {}) {
  const { requestUnsavedConfirm, createNotification } = useMainContext();
  const setDataStatus = useDataSetStatus();

  return useCallback(
    (plan, decide) => {
      const { action, formStashed, discard, message, warnings } = plan;
      const clearForm = () => {
        setDataStatus(statusField, false);
        setDataStatus("unsavedScope", null);
      };

      if (formStashed) {
        // The form's changes are in the draft: it is not unsaved any more
        clearForm();
      }

      if (action === "block") {
        warnings.forEach((warning) => createNotification("warning", warning));
        decide(false);
      } else if (action === "go") {
        decide(true);
      } else {
        requestUnsavedConfirm((proceed) => {
          decide(
            proceed,
            proceed
              ? () => {
                  discard.forEach((scope) => draft.discard(scope));
                  setTimeout(clearForm, 0);
                }
              : undefined
          );
        }, message);
      }
    },
    [createNotification, requestUnsavedConfirm, setDataStatus, statusField]
  );
}

// confirmsNavigation(destination, cb, { leaveAll, state, replace }): asks to
// confirm when the form has unsaved changes, or when the destination leaves
// a scope whose draft is not saved (a property draft stays on the property
// and its variant pages, see draft/scopes.js). destination "" is not a
// navigation (e.g. a tab of the page): it leaves no draft. leaveAll: every
// draft is left (change of branch, logout). state, replace: the options of
// the navigation (e.g. the inner tab of the variant page to open, in the
// state of the location: a query string is taken by the Item as the id of a
// row to copy). On "Exit Without Saving" the drafts left are discarded, with
// the draft of the modified form's scope (see draft/exit.js); the
// confirmation names them.
// A modified form whose draft scope the destination stays in (a variant tab,
// the property and its variant pages) is first asked to put its changes
// into the draft (draft.stashOpenForms, see draft/stashForm.js): then it
// navigates with no question; a form that can't (invalid) asks as before; a
// change the draft refuses keeps the user on the page with a warning (see
// draft/navigation.js).
// The browser's Back and Forward ask by the same rules (NavigationGuard);
// the navigations made here are not asked again.
export default function useConfirmNavigation({
  resetStatus = true,
  statusField = "unsavedData"
} = {}) {
  const navigate = useNavigate();
  const haveUnsavedChanges = useDataStateSelector((state) => state.unsavedData);
  const unsavedScope = useDataStateSelector((state) => state.unsavedScope);
  const { branch } = useUserFields("branch");
  const carryOut = useCarryOutLeave({ statusField });

  const confirmsNavigation = useCallback(
    (destination, cb, { leaveAll = false, state, replace } = {}) => {
      const plan = planLeave({
        destination,
        leaveAll,
        formDirty: Boolean(haveUnsavedChanges),
        formScope: unsavedScope,
        branch
      });

      carryOut(plan, (proceed, discard) => {
        if (proceed) {
          discard?.();
          destination && navigate(destination, { state, replace });
        }
        cb?.(proceed);
      });
    },
    [branch, carryOut, haveUnsavedChanges, navigate, unsavedScope]
  );

  return confirmsNavigation;
}
